import React, { useState, useEffect } from 'react';
import { submitLeadQuietly } from "../lib/crm";
import { motion, AnimatePresence } from 'framer-motion';
import {
  Stethoscope, Globe, CheckCircle, FileText, Wallet, Calendar,
  MessageCircle, Scale, GraduationCap, ChevronDown, MapPin, Check,
  User, Phone, Mail, Navigation, HeartPulse, ShieldCheck, Map, ChevronRight, X, Loader2
} from 'lucide-react';

// Floating Background Component
const FloatingBackground = () => {
  const words = [
    "NEET Score", "MBBS India", "MBBS Abroad", "Budget", "Eligibility",
    "Drop Year?", "Private College?", "University", "Counselling",
    "Admission", "Visa", "Documentation", "Career Goals", "Parent Guidance",
    "Score Review", "Right Pathway", "Future Doctor", "Compare Options", "Clarity First"
  ];

  return (
    <div className="fixed inset-0 pointer-events-none z-[-1] overflow-hidden opacity-20">
      {words.map((word, i) => (
        <motion.div
          key={i}
          initial={{
            opacity: 0,
            x: Math.random() * window.innerWidth,
            y: window.innerHeight + 100,
          }}
          animate={{
            opacity: [0, 0.5, 0],
            y: -100,
            x: `calc(${Math.random() * 100}vw - 50px)`,
          }}
          transition={{
            duration: Math.random() * 20 + 20,
            repeat: Infinity,
            delay: Math.random() * 10,
            ease: "linear",
          }}
          className="absolute whitespace-nowrap text-[#6DBB2D] font-bold text-sm md:text-xl rounded-full bg-white px-4 py-2 shadow-sm border border-[#6DBB2D]/20"
        >
          {word}
        </motion.div>
      ))}
    </div>
  );
};

// Section 12: FAQ Data
const faqs = [
  {
    q: "Should I take a drop after NEET?",
    a: "A drop can help some students, but it is not the right decision for everyone. Before taking a drop, compare your current MBBS options, budget, eligibility, and long-term goals."
  },
  {
    q: "Does UniThink help with MBBS in India and abroad both?",
    a: "Yes. UniThink helps students explore MBBS pathways in India and abroad based on score, profile, budget, and preferences."
  },
  {
    q: "Can I explore MBBS abroad after NEET?",
    a: "Yes, many students explore MBBS abroad after NEET. The right country and university should be selected carefully after checking recognition, eligibility, budget, safety, language, and future exam requirements."
  },
  {
    q: "Is MBBS abroad valid for Indian students?",
    a: "Students should choose universities and countries carefully and verify current official regulations, recognition, licensing, and exam requirements before final admission."
  },
  {
    q: "What happens in the counselling session?",
    a: "Your NEET score, academic profile, budget, preferences, and goals are reviewed. Based on that, suitable MBBS pathways are discussed."
  },
  {
    q: "Is counselling useful for parents?",
    a: "Yes. MBBS admission involves cost, location, safety, documentation, and long-term planning, so parent involvement is very important."
  },
  {
    q: "Will UniThink help with documentation?",
    a: "Yes. UniThink provides guidance for documentation and admission-related formalities."
  },
  {
    q: "Does UniThink help with visa and travel for abroad admissions?",
    a: "Yes. For abroad admissions, UniThink provides guidance for visa, travel, pre-departure, and post-arrival support."
  }
];

// Identifies the counselling form to the CRM: becomes the campaign name and
// the key its Lead Routing rule matches on.
const CRM_FORM_ID = "mbbs-admissions";

const PAYMENT_WEBHOOK_URL = "https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec";

const MbbsAdmissions = () => {

  const [paymentModal, setPaymentModal] = useState({ open: false, type: null });
  const [payForm, setPayForm] = useState({ name: "", phone: "", email: "" });
  const [isPaying, setIsPaying] = useState(false);

  const openPaymentModal = (type) => {
    setPaymentModal({ open: true, type });
  };

  const handlePayment = () => {
    if (!payForm.name || !payForm.phone || !payForm.email) {
      alert("Please fill all details");
      return;
    }

    setIsPaying(true);
    const baseAmount = paymentModal.type === 'expert' ? 3500 : 30000;
    const amount = baseAmount + (baseAmount * 0.18); // Add 18% GST
    const planName = paymentModal.type === 'expert' ? "Expert MBBS Counselling" : "Complete Admission Support";

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: amount * 100, // paise
      currency: "INR",
      name: "UniThink",
      description: planName,
      handler: function (response) {
        // Send data to Google Sheets
        fetch(PAYMENT_WEBHOOK_URL, {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "text/plain" },
          body: JSON.stringify({
            studentName: payForm.name,
            mobileNumber: payForm.phone,
            email: payForm.email,
            planName: planName,
            paymentStatus: "Paid",
            paymentId: response ? response.razorpay_payment_id : 'test_pay_12345',
            amount: amount
          }),
        }).finally(() => {
          setIsPaying(false);
          setPaymentModal({ open: false, type: null });
          alert("Payment Successful! We have received your request.");
        });
      },
      prefill: {
        name: payForm.name,
        contact: payForm.phone,
        email: payForm.email
      },
      theme: { color: "#111111" },
      modal: {
        ondismiss: function () {
          setIsPaying(false);
          fetch(PAYMENT_WEBHOOK_URL, {
            method: "POST",
            mode: "no-cors",
            headers: { "Content-Type": "text/plain" },
            body: JSON.stringify({
              studentName: payForm.name,
              mobileNumber: payForm.phone,
              email: payForm.email,
              planName: planName,
              paymentStatus: "Abandoned",
              paymentId: "N/A",
              amount: amount
            }),
          });
        }
      }
    };

    // SMOKE TEST BYPASS
    if (payForm.name.trim().toLowerCase() === "test user") {
      options.handler(null);
      return;
    }

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (response) {
      setIsPaying(false);
      fetch(PAYMENT_WEBHOOK_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({
          studentName: payForm.name,
          mobileNumber: payForm.phone,
          email: payForm.email,
          planName: planName,
          paymentStatus: "Failed",
          paymentId: response.error.metadata.payment_id || "N/A",
          amount: amount
        }),
      });
      alert("Payment Failed. Please try again.");
    });
    rzp.open();
  };

  const [activeFaq, setActiveFaq] = useState(null);

  const [formData, setFormData] = useState({
    studentName: '',
    parentName: '',
    mobileNumber: '',
    email: '',
    city: '',
    neetScore: '',
    preferredOption: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [gotcha, setGotcha] = useState(""); // bot trap; kept out of the Sheet payload

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Replace this with the URL generated from the mbbs-admissions-apps-script.gs deployment
    // You can also use an existing Apps Script URL if you prefer.
    const GAS_WEB_APP_URL = "https://script.google.com/macros/s/AKfycbw-nbvXxRyG2z7-aE03RXPVLSS_1dxAm1CDeqOHbSOxSrw_vOqvWMttbbj_XIM0LFNi/exec";

    try {
      // Send data to Apps Script
      if (GAS_WEB_APP_URL === "YOUR_GOOGLE_APPS_SCRIPT_URL_HERE") {
        console.warn("Please update the GAS_WEB_APP_URL with your deployed Google Apps Script URL.");
        // Simulate a delay for demo purposes since the URL is not set
        await new Promise(resolve => setTimeout(resolve, 1500));
      } else {
        const formBody = new URLSearchParams();
        Object.keys(formData).forEach(key => formBody.append(key, formData[key]));
        formBody.append('source', 'MBBS_Admissions_Landing_Page');

        // Double-writing while the CRM proves itself: the Sheet is still the
        // source of truth, so only its outcome decides what the visitor sees.
        const [sheetResult] = await Promise.allSettled([
          fetch(GAS_WEB_APP_URL, {
            method: 'POST',
            mode: 'no-cors',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formBody.toString(),
          }),
          submitLeadQuietly({ formId: CRM_FORM_ID, fields: { ...formData, _gotcha: gotcha } }),
        ]);
        if (sheetResult.status === "rejected") throw sheetResult.reason;
      }

      alert("Thank you. Your counselling request has been received. Our team will contact you shortly.");
      setFormData({
        studentName: '', parentName: '', mobileNumber: '', email: '',
        city: '', neetScore: '', preferredOption: '',
        budget: '', message: ''
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Thank you. Your request was noted, but there was an issue submitting. We will contact you soon.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    document.title = "MBBS Admission Guidance in India & Abroad | UniThink Education International";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = "Explore MBBS admission options in India and abroad with UniThink. Get profile-based counselling based on NEET score, budget, eligibility, university selection, documentation, and admission support.";
  }, []);


  // Animation variants
  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const scrollToForm = () => {
    document.getElementById('booking-form').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="font-['Inter',sans-serif] bg-white text-[#111111] overflow-x-hidden pt-[80px]">

      <AnimatePresence>
        {paymentModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative"
            >
              <button
                onClick={() => setPaymentModal({ open: false, type: null })}
                className="absolute top-6 right-6 text-[#111111]/50 hover:text-[#111111] transition-colors"
              >
                <X size={24} />
              </button>

              <h3 className="text-2xl font-black mb-2">Complete Your Booking</h3>
              <p className="text-[#111111]/70 mb-6 text-sm">
                You are booking the <span className="font-bold text-[#111111]">{paymentModal.type === 'expert' ? "Expert MBBS Counselling" : "Complete Admission Support"}</span> plan.
              </p>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-bold text-[#111111] mb-1">Student Name *</label>
                  <input
                    type="text"
                    value={payForm.name}
                    onChange={(e) => setPayForm({ ...payForm, name: e.target.value })}
                    className="w-full border border-[#111111]/20 rounded-xl px-4 py-3 outline-none focus:border-[#6DBB2D] focus:ring-1 focus:ring-[#6DBB2D] transition-all"
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#111111] mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    value={payForm.phone}
                    onChange={(e) => setPayForm({ ...payForm, phone: e.target.value })}
                    className="w-full border border-[#111111]/20 rounded-xl px-4 py-3 outline-none focus:border-[#6DBB2D] focus:ring-1 focus:ring-[#6DBB2D] transition-all"
                    placeholder="Enter WhatsApp number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#111111] mb-1">Email Address *</label>
                  <input
                    type="email"
                    value={payForm.email}
                    onChange={(e) => setPayForm({ ...payForm, email: e.target.value })}
                    className="w-full border border-[#111111]/20 rounded-xl px-4 py-3 outline-none focus:border-[#6DBB2D] focus:ring-1 focus:ring-[#6DBB2D] transition-all"
                    placeholder="Enter email address"
                  />
                </div>
              </div>

              <button
                onClick={handlePayment}
                disabled={isPaying}
                className="w-full bg-[#111111] text-white font-bold text-lg py-4 rounded-xl shadow-md flex justify-center items-center gap-2 hover:bg-[#333] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isPaying ? <Loader2 size={24} className="animate-spin" /> : null}
                Pay ₹{paymentModal.type === 'expert' ? "4,130" : "35,400"} (Incl. 18% GST)
              </button>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-[#111111]/50 font-medium">
                <ShieldCheck size={14} className="text-[#6DBB2D]" /> Secured by Razorpay
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      <FloatingBackground />

      {/* SECTION 1: Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-10 pb-20 overflow-hidden">
        <div className="w-full mx-auto px-4 max-w-[1920px] px-4 md:px-12 lg:px-24 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div
              className="w-full lg:w-1/2 text-center lg:text-left"
              initial="hidden" animate="visible" variants={fadeUp}
            >
              <div className="inline-block bg-[#FFC928]/20 text-[#111111] font-bold px-4 py-2 rounded-full mb-6 border border-[#FFC928]">
                India & Abroad Options Available
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-[900] leading-tight mb-6 text-[#111111]">
                Before Taking a Drop, Know Your <span className="text-[#6DBB2D]">MBBS Options.</span>
              </h1>
              <h2 className="text-xl md:text-2xl font-bold mb-6 text-[#111111]/80">
                Explore MBBS Admissions in India & Abroad with Expert Guidance.
              </h2>
              <p className="text-lg mb-8 text-[#111111]/70 max-w-xl mx-auto lg:mx-0">
                Your NEET score matters, but it should not make you panic or give up too soon. UniThink helps students and parents compare MBBS pathways based on score, budget, eligibility, and long-term career goals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={scrollToForm}
                  className="bg-[#6DBB2D] text-white font-bold text-lg px-8 py-4 rounded-xl shadow-lg shadow-[#6DBB2D]/30 flex items-center justify-center gap-2"
                >
                  Book MBBS Counselling <ChevronRight size={20} />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => document.getElementById('comparison').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white text-[#111111] border-2 border-[#111111] font-bold text-lg px-8 py-4 rounded-xl flex items-center justify-center gap-2"
                >
                  Explore India & Abroad <Globe size={20} />
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              className="w-full lg:w-1/2 relative mt-12 lg:mt-0"
              initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}
              id="booking-form"
            >
              <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 border border-[#111111]/10 relative z-20">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#6DBB2D]/20 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#FFC928]/20 rounded-full blur-3xl"></div>


                <div className="relative z-10">
                  <h3 className="text-2xl font-black mb-1 text-[#111111]">Book Free MBBS Counselling</h3>
                  <p className="text-[#111111]/70 mb-5 text-sm">
                    Share a few details and our team will guide you on the right MBBS pathway.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Bot trap: hidden from people, so anything typed here is spam. */}
                    <input
                      type="text"
                      name="_gotcha"
                      value={gotcha}
                      onChange={(e) => setGotcha(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="studentName" className="block text-sm font-bold text-[#111111] mb-1">Student Name *</label>
                        <input id="studentName" name="studentName" type="text" required value={formData.studentName} onChange={handleChange}
                          className="w-full border border-[#111111]/20 rounded-xl px-4 py-3 outline-none focus:border-[#6DBB2D] focus:ring-1 focus:ring-[#6DBB2D] transition-all" placeholder="Enter full name" />
                      </div>
                      <div>
                        <label htmlFor="parentName" className="block text-sm font-bold text-[#111111] mb-1">Parent Name</label>
                        <input id="parentName" name="parentName" type="text" value={formData.parentName} onChange={handleChange}
                          className="w-full border border-[#111111]/20 rounded-xl px-4 py-3 outline-none focus:border-[#6DBB2D] focus:ring-1 focus:ring-[#6DBB2D] transition-all" placeholder="Enter parent name" />
                      </div>
                      <div>
                        <label htmlFor="mobileNumber" className="block text-sm font-bold text-[#111111] mb-1">Mobile Number *</label>
                        <input id="mobileNumber" name="mobileNumber" type="tel" required value={formData.mobileNumber} onChange={handleChange}
                          className="w-full border border-[#111111]/20 rounded-xl px-4 py-3 outline-none focus:border-[#6DBB2D] focus:ring-1 focus:ring-[#6DBB2D] transition-all" placeholder="Enter WhatsApp number" />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-bold text-[#111111] mb-1">Email Address *</label>
                        <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange}
                          className="w-full border border-[#111111]/20 rounded-xl px-4 py-3 outline-none focus:border-[#6DBB2D] focus:ring-1 focus:ring-[#6DBB2D] transition-all" placeholder="Enter email address" />
                      </div>
                      <div>
                        <label htmlFor="city" className="block text-sm font-bold text-[#111111] mb-1">City</label>
                        <input id="city" name="city" type="text" value={formData.city} onChange={handleChange}
                          className="w-full border border-[#111111]/20 rounded-xl px-4 py-3 outline-none focus:border-[#6DBB2D] focus:ring-1 focus:ring-[#6DBB2D] transition-all" placeholder="Enter your city" />
                      </div>
                      <div>
                        <label htmlFor="neetScore" className="block text-sm font-bold text-[#111111] mb-1">NEET Score</label>
                        <input id="neetScore" name="neetScore" type="text" value={formData.neetScore} onChange={handleChange}
                          className="w-full border border-[#111111]/20 rounded-xl px-4 py-3 outline-none focus:border-[#6DBB2D] focus:ring-1 focus:ring-[#6DBB2D] transition-all" placeholder="Enter score or Not attempted" />
                      </div>
                      <div>
                        <label htmlFor="preferredOption" className="block text-sm font-bold text-[#111111] mb-1">Preferred Option</label>
                        <select id="preferredOption" name="preferredOption" value={formData.preferredOption} onChange={handleChange}
                          className="w-full border border-[#111111]/20 rounded-xl px-4 py-3 outline-none focus:border-[#6DBB2D] focus:ring-1 focus:ring-[#6DBB2D] transition-all bg-white">
                          <option value="">Select an option</option>
                          <option value="MBBS in India">MBBS in India</option>
                          <option value="MBBS Abroad">MBBS Abroad</option>
                          <option value="Not sure yet">Not sure yet</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="budget" className="block text-sm font-bold text-[#111111] mb-1">Budget</label>
                        <select id="budget" name="budget" value={formData.budget} onChange={handleChange}
                          className="w-full border border-[#111111]/20 rounded-xl px-4 py-3 outline-none focus:border-[#6DBB2D] focus:ring-1 focus:ring-[#6DBB2D] transition-all bg-white">
                          <option value="">Select a range</option>
                          <option value="Under 25 Lakhs">Under &#8377;25 Lakhs</option>
                          <option value="25-50 Lakhs">&#8377;25&#8211;50 Lakhs</option>
                          <option value="50 Lakhs - 1 Crore">&#8377;50 Lakhs &#8211; 1 Crore</option>
                          <option value="Above 1 Crore">Above &#8377;1 Crore</option>
                          <option value="Not decided">Not decided</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-bold text-[#111111] mb-1">Message</label>
                      <textarea id="message" name="message" rows={3} value={formData.message} onChange={handleChange}
                        className="w-full border border-[#111111]/20 rounded-xl px-4 py-3 outline-none focus:border-[#6DBB2D] focus:ring-1 focus:ring-[#6DBB2D] transition-all resize-none" placeholder="Anything you would like us to know?" />
                    </div>

                    <button type="submit" disabled={isSubmitting}
                      className="w-full bg-[#111111] text-white font-bold text-lg py-4 rounded-xl shadow-md flex justify-center items-center gap-2 hover:bg-[#333] transition-colors disabled:opacity-70 disabled:cursor-not-allowed">
                      {isSubmitting ? <Loader2 size={24} className="animate-spin" /> : null}
                      {isSubmitting ? "Submitting..." : "Book Free Counselling"}
                    </button>
                  </form>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Trust Strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
            className="mt-20 border-t border-b border-[#F6F7F8] py-6 flex flex-wrap justify-center gap-4 md:gap-10 text-base md:text-lg font-bold text-[#111111]/60"
          >
            <span className="flex items-center gap-2"><CheckCircle size={18} className="text-[#6DBB2D]" /> Profile-Based Guidance</span>
            <span className="flex items-center gap-2"><CheckCircle size={18} className="text-[#6DBB2D]" /> India & Abroad Options</span>
            <span className="flex items-center gap-2"><CheckCircle size={18} className="text-[#6DBB2D]" /> University Shortlisting</span>
            <span className="flex items-center gap-2"><CheckCircle size={18} className="text-[#6DBB2D]" /> Admission Support</span>
          </motion.div>
        </div>
      </section>

      {/* SECTION 1.5: Top Destinations */}
      <section className="py-20 bg-[#3B0A6D] text-white overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="w-full mx-auto px-4 max-w-[1600px] px-4 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-[900] mb-4">Top Choices for <span className="text-[#FFC928]">MBBS Abroad</span></h2>
            <p className="text-xl font-bold mb-2">6-Year Program | Get Your Eligibility Checked & University Shortlist Now!</p>
            <p className="text-lg opacity-90 max-w-5xl mx-auto mb-8">
              Explore the most popular and trusted destinations for Indian students seeking excellent clinical exposure and affordable fees (INR 15 LACS - 25 LACS - 35 LACS).
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            {[
              { name: "Russia", flag: "🇷🇺" },
              { name: "Georgia", flag: "🇬🇪" },
              { name: "Uzbekistan", flag: "🇺🇿" },
              { name: "Kazakhstan", flag: "🇰🇿" },
              { name: "Vietnam", flag: "🇻🇳" },
              { name: "Philippines", flag: "🇵🇭" },
              { name: "Germany", flag: "🇩🇪" },
              { name: "Poland", flag: "🇵🇱" },
              { name: "Nepal", flag: "🇳🇵" }
            ].map((country, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -5, scale: 1.05 }}
                className="bg-white/10 backdrop-blur-md border border-white/20 px-8 py-4 rounded-full flex items-center gap-3 cursor-pointer hover:bg-[#FFC928] hover:text-[#111111] transition-all shadow-lg"
              >
                <span className="text-3xl">{country.flag}</span>
                <span className="font-[900] text-xl uppercase tracking-wide">{country.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* SECTION 11.5: Pricing / Plans */}
      <section className="py-24 bg-[#F6F7F8]">
        <div className="w-full mx-auto px-4 max-w-[1400px] px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-[900] mb-6">Choose Your <span className="text-[#6DBB2D]">MBBS Guidance Plan</span></h2>
            <p className="text-lg opacity-80 max-w-4xl mx-auto">
              Not every student needs the same level of support. Whether you're looking for expert clarity or complete admission assistance, choose the guidance plan that best fits your MBBS journey.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Card 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-[#111111]/5 flex flex-col relative"
            >
              <h3 className="text-2xl font-black mb-2">Expert MBBS Counselling</h3>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-4xl font-[900]">₹3,500</span>
                <span className="text-[#111111]/60 font-medium">+ GST</span>
              </div>
              <div className="mb-6 p-4 bg-[#FFC928]/10 rounded-xl border border-[#FFC928]/20 text-[#111111]/80 text-sm">
                <strong className="block text-[#111111] mb-1 text-base">Best For</strong>
                Students and parents who want expert guidance before making an MBBS admission decision.
              </div>

              <div className="mb-8 flex-grow">
                <strong className="block mb-4 text-lg">What's Included</strong>
                <ul className="space-y-3">
                  {[
                    "60–90 Minute Personalized Online Counselling",
                    "NEET Score & Rank Analysis",
                    "Category & Eligibility Assessment",
                    "Budget Planning & Fee Estimation",
                    "Government vs Private vs Deemed vs Abroad Comparison",
                    "State-wise Admission Possibilities",
                    "Personalized College Recommendations",
                    "MBBS Admission Roadmap",
                    "Dedicated Parent Q&A Session",
                    "Senior MBBS Counsellor Consultation"
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#6DBB2D] flex-shrink-0 mt-0.5" />
                      <span className="text-base text-[#111111]/80">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-auto pt-6 border-t border-[#111111]/10">
                <p className="text-sm font-medium text-[#111111]/70 mb-6 italic">
                  <strong className="text-[#111111]">Special Benefit:</strong> If you enroll in our Complete MBBS Admission Support Package, your ₹3,500 counselling fee will be adjusted against the package fee.
                </p>
                <button onClick={() => openPaymentModal('expert')} className="w-full bg-[#111111] text-white font-bold text-lg py-4 rounded-xl shadow-md hover:bg-[#333] transition-colors">
                  Book Expert Counselling
                </button>
              </div>
            </motion.div>

            {/* Card 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
              className="bg-[#111111] text-white rounded-3xl p-8 md:p-10 shadow-2xl border border-[#FFC928]/30 flex flex-col relative transform lg:-translate-y-4"
            >
              <div className="absolute top-0 right-8 transform -translate-y-1/2 bg-[#FFC928] text-[#111111] font-black text-sm px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1">
                ⭐ Most Popular
              </div>

              <h3 className="text-2xl font-black mb-2 text-white">Complete Admission Support</h3>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-4xl font-[900] text-[#FFC928]">₹30,000</span>
                <span className="text-white/60 font-medium">+ GST</span>
              </div>
              <div className="mb-6 p-4 bg-white/10 rounded-xl border border-white/20 text-white/80 text-sm">
                <strong className="block text-white mb-1 text-base">Best For</strong>
                Students who want complete professional guidance throughout the MBBS admission process.
              </div>

              <div className="mb-8 flex-grow">
                <strong className="block mb-4 text-lg text-white">What's Included</strong>
                <p className="text-sm text-[#FFC928] font-medium mb-4">Everything included in Expert Counselling, plus:</p>
                <ul className="space-y-3">
                  {[
                    "Personalized Admission Strategy",
                    "State-wise Counselling Planning",
                    "College Shortlisting",
                    "Government, Private & Deemed College Guidance",
                    "Multi-State Admission Planning",
                    "Choice Filling Assistance",
                    "Round-wise Counselling Support",
                    "Document Verification Guidance",
                    "Seat Allotment Support",
                    "Admission Process Assistance",
                    "Dedicated Admission Counsellor",
                    "Phone & WhatsApp Support",
                    "Senior MBBS Admission Expert Guidance"
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#FFC928] flex-shrink-0 mt-0.5" />
                      <span className="text-base text-white/90">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-auto pt-6 border-t border-white/20">
                <div className="mb-6">
                  <strong className="block text-white mb-2">Ideal For:</strong>
                  <ul className="space-y-1 text-sm text-white/70 font-medium">
                    <li>✔ Students applying in multiple states</li>
                    <li>✔ Students seeking the best possible college within budget</li>
                    <li>✔ Parents looking for complete admission assistance</li>
                  </ul>
                </div>
                <button onClick={() => openPaymentModal('complete')} className="w-full bg-[#FFC928] text-[#111111] font-black text-lg py-4 rounded-xl shadow-[0_0_20px_rgba(255,201,40,0.4)] hover:bg-yellow-400 transition-colors">
                  Request Admission Support
                </button>
              </div>
            </motion.div>
          </div>

          <div className="mt-16 text-center max-w-3xl mx-auto bg-white p-8 rounded-2xl shadow-sm border border-[#111111]/5">
            <h4 className="text-2xl font-black mb-3">Not Sure Which Plan Is Right for You?</h4>
            <p className="text-lg text-[#111111]/70 mb-2">No problem.</p>
            <p className="text-base text-[#111111]/80">Fill out the enquiry form and our MBBS Admission Team will help you choose the right guidance plan based on your NEET score, category, budget, and career goals.</p>
          </div>
        </div>
      </section>

      {/* SECTION 2: Problem Statement */}
      <section className="py-20 bg-[#F6F7F8]">
        <div className="w-full mx-auto px-4 max-w-[1600px] px-4 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="text-3xl md:text-5xl font-[900] mb-6">Confused After NEET? <span className="text-[#F72C85]">You Are Not Alone.</span></h2>
            <p className="text-lg text-[#111111]/70 mb-12 max-w-2xl mx-auto">
              Every year, many students and parents face the same confusion after NEET. A big decision should not be made in panic. It should be made with clarity.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Should I take a drop?",
              "Should I choose private MBBS in India?",
              "Is MBBS abroad a safe option?",
              "Which country or university is right?",
              "What will be the total cost?",
              "Will the degree be valid?"
            ].map((q, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white p-6 rounded-2xl shadow-sm border border-[#111111]/5 flex items-start gap-4"
              >
                <MessageCircle className="text-[#FFC928] flex-shrink-0 mt-1" />
                <p className="text-left font-bold text-lg">{q}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: Emotional Hook */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="w-full mx-auto px-4 max-w-[1600px] px-4 md:px-12">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="lg:w-1/2"
            >
              <h2 className="text-3xl md:text-5xl font-[900] mb-6">One Score Is Not Your <span className="text-[#6DBB2D]">Entire Future.</span></h2>
              <p className="text-lg text-[#111111]/70 mb-6 leading-relaxed">
                Your NEET result is important, but it is only one part of your journey. The right MBBS pathway depends on your complete profile — score, budget, eligibility, family preference, and future goals.
              </p>
              <p className="text-lg text-[#111111]/70 mb-8 leading-relaxed font-semibold">
                Before you decide anything, first compare what is actually available for you.
              </p>

              <div className="bg-[#FFC928]/10 border-l-4 border-[#FFC928] p-6 rounded-r-xl">
                <p className="font-bold text-xl text-[#111111]">
                  Do not choose emotionally. Choose after comparing properly.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="lg:w-1/2 w-full flex justify-center"
            >
              <div className="relative w-full max-w-md aspect-square bg-[#F6F7F8] rounded-full flex items-center justify-center p-8">
                <Scale className="absolute text-[#6DBB2D]/20 w-3/4 h-3/4" />
                <div className="relative z-10 flex flex-col md:flex-row items-center justify-between w-full gap-8">
                  <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 4 }} className="bg-white p-6 rounded-xl shadow-lg text-center w-full">
                    <HeartPulse className="mx-auto text-[#F72C85] mb-2" size={32} />
                    <p className="font-bold">NEET Score</p>
                  </motion.div>
                  <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 4, delay: 0.5 }} className="bg-[#111111] text-white p-6 rounded-xl shadow-lg text-center w-full">
                    <Wallet className="mx-auto text-[#FFC928] mb-2" size={32} />
                    <p className="font-bold">Budget & Goals</p>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 4 & 5: Solution & Compare */}
      <section className="py-20 bg-[#6DBB2D] text-white">
        <div className="w-full mx-auto px-4 max-w-[1600px] px-4 md:px-12">
          <div className="text-center mb-16">
            <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-3xl md:text-5xl font-[900] mb-6">
              UniThink Helps You Choose With Clarity.
            </motion.h2>
            <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-xl max-w-5xl mx-auto opacity-90 mb-12">
              Before choosing India, abroad, private college, deemed university, or drop year, compare the right factors. Your MBBS Pathway Should Match Your Profile.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "NEET Score Review", desc: "Understand how your NEET score affects your available options.", icon: <FileText size={32} /> },
              { title: "Budget Planning", desc: "Compare tuition fees, hostel, food, living expenses, visa, and other costs.", icon: <Wallet size={32} /> },
              { title: "Eligibility Check", desc: "Check academic and admission requirements before applying.", icon: <CheckCircle size={32} /> },
              { title: "India vs Abroad Options", desc: "Compare MBBS pathways in India and abroad based on your profile.", icon: <Globe size={32} /> },
              { title: "University Shortlisting", desc: "Choose based on recognition, location, facilities, and student support.", icon: <GraduationCap size={32} /> },
              { title: "Long-Term Career Goals", desc: "Understand how your choice may affect your future medical career pathway.", icon: <Map size={32} /> },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-white text-[#111111] p-8 rounded-2xl shadow-xl flex flex-col items-start"
              >
                <div className="bg-[#F6F7F8] p-4 rounded-xl text-[#6DBB2D] mb-6">
                  {card.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{card.title}</h3>
                <p className="text-base text-[#111111]/70">{card.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <motion.button
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={scrollToForm}
              className="bg-[#111111] text-white font-bold text-lg px-8 py-4 rounded-xl shadow-xl"
            >
              Start With Expert Counselling
            </motion.button>
          </div>
        </div>
      </section>

      {/* SECTION 6: India vs Abroad Comparison */}
      <section id="comparison" className="py-24 bg-white">
        <div className="w-full mx-auto px-4 max-w-[1600px] px-4 md:px-12">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="text-3xl md:text-5xl font-[900] mb-6 text-center">MBBS in India or Abroad? <span className="text-[#FFC928]">Compare Before You Decide.</span></h2>
            <p className="text-lg text-[#111111]/70 max-w-5xl mx-auto text-center mb-12">
              The right option is different for every student. Some students may be better suited for India. Some may find MBBS abroad more practical. Some may benefit from a drop year. Others may lose valuable time.
            </p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="overflow-x-auto rounded-2xl shadow-xl border border-[#F6F7F8]"
          >
            <table className="w-full min-w-[700px] text-left">
              <thead>
                <tr className="bg-[#111111] text-white">
                  <th className="p-6 text-lg font-bold w-1/4">Factor</th>
                  <th className="p-6 text-lg font-bold w-3/8 border-l border-white/20">MBBS in India</th>
                  <th className="p-6 text-lg font-bold w-3/8 border-l border-white/20">MBBS Abroad</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F6F7F8]">
                {[
                  { f: "Competition", i: "Very high", a: "Depends on country and university" },
                  { f: "Cost", i: "Govt seats are affordable, private/deemed can be expensive", a: "Often structured and comparatively affordable depending on country" },
                  { f: "Admission Route", i: "NEET + counselling process", a: "NEET qualification + university/country process" },
                  { f: "Exposure", i: "Indian healthcare system", a: "International academic exposure" },
                  { f: "Key Concern", i: "High competition and high private fees", a: "Recognition, safety, FMGE/NExT preparation, country choice" },
                  { f: "Best For", i: "Students with strong rank or suitable budget", a: "Students looking for alternate MBBS pathways after proper evaluation" }
                ].map((row, i) => (
                  <tr key={i} className="hover:bg-[#F6F7F8] transition-colors">
                    <td className="p-6 font-bold bg-[#F6F7F8]/50">{row.f}</td>
                    <td className="p-6 border-l border-[#F6F7F8]">{row.i}</td>
                    <td className="p-6 border-l border-[#F6F7F8]">{row.a}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          <div className="w-full flex justify-center mt-8">
            <div className="text-center bg-[#FFC928]/20 py-4 px-6 rounded-xl inline-block max-w-5xl font-bold text-lg border border-[#FFC928]">
              The best decision is not India or abroad. The best decision is the one that fits your profile.
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: Student Scenarios */}
      <section className="py-20 bg-[#F6F7F8]">
        <div className="w-full mx-auto px-4 max-w-[1600px] px-4 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-[900] mb-6">Every Student Needs a <span className="text-[#6DBB2D]">Different Plan.</span></h2>
            <p className="text-xl text-[#111111]/70 font-semibold">The right MBBS plan is profile-based, not random.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {[
              { title: "Student A: Good NEET Score", desc: "Can explore government, private, or deemed university options in India with proper counselling strategy." },
              { title: "Student B: Moderate NEET Score", desc: "Should compare Indian private options with selected MBBS abroad pathways." },
              { title: "Student C: Low NEET Score but Strong Dream", desc: "Should not panic. A profile evaluation can help understand realistic options, drop decision, and abroad possibilities." },
              { title: "Student D: Budget-Conscious Family", desc: "Needs a transparent cost comparison before committing to any college, country, or university." },
            ].map((scenario, i) => (
              <motion.div
                key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                whileHover={{ scale: 1.02 }}
                className="bg-white p-8 rounded-2xl shadow-sm border-l-4 border-[#6DBB2D] flex flex-col"
              >
                <div className="bg-[#6DBB2D]/10 w-12 h-12 rounded-full flex items-center justify-center text-[#6DBB2D] mb-4">
                  <User size={24} />
                </div>
                <h3 className="text-2xl font-bold mb-3">{scenario.title}</h3>
                <p className="text-[#111111]/70 text-lg leading-relaxed">{scenario.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8: Timeline */}
      <section className="py-24 bg-white">
        <div className="w-full mx-auto px-4 max-w-[1600px] px-4 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-[900] mb-6">From Confusion to a <span className="text-[#3B0A6D]">Clear Admission Plan</span></h2>
          </div>

          <div className="relative">
            {/* Desktop horizontal line */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-[#F6F7F8] -translate-y-1/2 z-0"></div>
            {/* Mobile vertical line */}
            <div className="md:hidden absolute top-0 left-6 w-1 h-full bg-[#F6F7F8] z-0"></div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 relative z-10">
              {[
                { step: "1", title: "Book Counselling", desc: "Fill the form and schedule your session." },
                { step: "2", title: "Profile Evaluation", desc: "We understand your score, budget, and goals." },
                { step: "3", title: "Compare Options", desc: "Understand realistic pathways in India & Abroad." },
                { step: "4", title: "Shortlist Universities", desc: "Right-fit options based on recognition and budget." },
                { step: "5", title: "Admission Planning", desc: "Guidance with applications and documents." },
                { step: "6", title: "Visa & Travel Support", desc: "Pre-departure and post-arrival assistance." },
              ].map((item, i) => (
                <motion.div
                  key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="flex flex-row md:flex-col items-start md:items-center gap-4 text-left md:text-center p-4 bg-white rounded-xl shadow-lg border border-[#F6F7F8] md:min-h-[220px]"
                >
                  <div className="w-12 h-12 bg-[#FFC928] text-[#111111] font-[900] text-xl rounded-full flex items-center justify-center shrink-0 shadow-lg border-4 border-white">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                    <p className="text-base text-[#111111]/70">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 9 & 10: Services & Why Choose Us */}
      <section className="py-20 bg-[#111111] text-white">
        <div className="w-full mx-auto px-4 max-w-[1600px] px-4 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-[900] mb-6">Why Students & Parents <span className="text-[#6DBB2D]">Choose UniThink</span></h2>
            <p className="text-lg opacity-80 max-w-5xl mx-auto mb-6">
              MBBS admission is one of the biggest decisions. We focus on clarity-first counselling so you can compare options properly. We help you make an informed decision, not a rushed one.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-4">
              {[
                "India and abroad MBBS options under one roof",
                "Profile-based counselling approach",
                "Clear comparison of score, budget, eligibility, and goals",
                "Support for university shortlisting",
                "Documentation and admission guidance",
                "Visa and travel assistance for abroad admissions",
                "Student and parent-friendly counselling",
                "Transparent discussion before decision-making"
              ].map((point, i) => (
                <motion.div
                  key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="flex items-center gap-4 bg-white/5 p-4 rounded-xl"
                >
                  <ShieldCheck className="text-[#6DBB2D] flex-shrink-0" />
                  <span className="font-semibold">{point}</span>
                </motion.div>
              ))}
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-[#6DBB2D] rounded-2xl blur-2xl opacity-20"></div>
              <img
                src="https://firebasestorage.googleapis.com/v0/b/unithink-89630.appspot.com/o/UniThink%20courses%20images%2Fmedicine_icon.png?alt=media"
                alt="Medical Shield"
                className="relative z-10 w-full max-w-md mx-auto drop-shadow-2xl brightness-110"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 12: FAQ Section */}
      <section className="py-24 bg-white">
        <div className="w-full mx-auto px-4 max-w-[1400px] px-4 md:px-8">
          <h2 className="text-3xl md:text-5xl font-[900] mb-12 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-[#111111]/10 rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full text-left p-6 bg-white hover:bg-[#F6F7F8] flex items-center justify-between font-bold text-lg transition-colors"
                >
                  {faq.q}
                  <ChevronDown className={`transform transition-transform ${activeFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {activeFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="bg-[#F6F7F8] px-6"
                    >
                      <p className="py-6 text-[#111111]/70 leading-relaxed border-t border-[#111111]/5">
                        {faq.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 13: Final CTA */}
      <section className="py-24 bg-[#FFC928] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        <div className="w-full mx-auto px-4 max-w-[1400px] px-4 md:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-[900] mb-6 text-[#111111]">Before You Decide, <span className="text-white drop-shadow-md">Take Expert Guidance.</span></h2>
          <p className="text-xl text-[#111111]/80 mb-10 max-w-5xl mx-auto font-medium">
            Do not make your MBBS decision based on fear, panic, or incomplete information. Whether you are considering India, abroad, private colleges, deemed universities, or a drop year — first understand your options clearly.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={scrollToForm}
            className="bg-[#111111] text-white font-bold text-xl px-10 py-5 rounded-xl shadow-2xl"
          >
            Book MBBS Counselling Now
          </motion.button>
        </div>
      </section>

      {/* Footer Disclaimer */}
      <div className="bg-[#111111] text-white/70 text-base md:text-lg text-center py-8 px-4">
        <div className="max-w-[1600px] px-4 md:px-12 mx-auto">
          <p className="mb-3 font-bold text-white text-lg md:text-xl">UniThink Education International</p>
          <p className="leading-relaxed">Disclaimer: Admission eligibility, fees, university recognition, visa rules, and regulatory requirements are subject to change. Students and parents should verify all final details from official sources before admission confirmation.</p>
        </div>
      </div>
    </div>
  );
};

export default MbbsAdmissions;
